import { useCallback, useEffect, useMemo, useState } from 'react';
import { getContactEmailDisplay, getUsableEmail } from '@car-pool/shared/contactIdentity';
import { formatContactName } from '@car-pool/shared/schoolRecords';
import { apiFetch } from '../config/api';
import ResetCodeButton from '../components/ResetCodeButton';
import SchoolAvatar from '../components/SchoolAvatar';
import SchoolDirectoryRow from '../components/SchoolDirectoryRow';
import SchoolInfoRow from '../components/SchoolInfoRow';
import SendOnboardingEmailButton from '../components/SendOnboardingEmailButton';
import SendOnboardingWhatsAppButton from '../components/SendOnboardingWhatsAppButton';
import {
  buildAuthorizedChildRows,
  buildDriverDirectory,
  filterDriverDirectory,
  getDriverAuthorizationStatus,
} from '../lib/driverDirectory';
import DriverAuthorizedChildren from '../components/DriverAuthorizedChildren';

export default function DriverList({ token, navigate, schoolId, selectDriverId = null }) {
  const [contacts, setContacts] = useState([]);
  const [students, setStudents] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState(selectDriverId);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [contactsRes, studentsRes, authorizationsRes] = await Promise.all([
        apiFetch(`/contacts?school_id=${schoolId}`, {}, token),
        apiFetch(`/students?school_id=${schoolId}`, {}, token),
        apiFetch(`/temporary-authorized-drivers/school/${schoolId}`, {}, token).catch(() => ({ data: [] })),
      ]);
      setContacts(contactsRes.data || []);
      setStudents(studentsRes.data || []);
      setAuthorizations(authorizationsRes.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load authorized drivers');
    } finally {
      setLoading(false);
    }
  }, [schoolId, token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (selectDriverId) setSelectedDriverId(selectDriverId);
  }, [selectDriverId]);

  const entries = useMemo(
    () => {
      const built = buildDriverDirectory(contacts, students, authorizations);
      // The directory lists people who have someone to collect. A driver opened
      // by id may have nobody yet — a brand-new account, or one whose
      // authorizations have all lapsed — and must still be the one shown, not
      // whoever happens to sit at the top of the list.
      const requested = selectDriverId
        && !built.some((entry) => Number(entry.driver.id) === Number(selectDriverId))
        && contacts.find((contact) => Number(contact.id) === Number(selectDriverId));
      if (requested) {
        built.push({
          driver: requested,
          ownChildrenIds: new Set(),
          authorizations: [],
          students: [],
          parents: [],
        });
      }
      return built;
    },
    [authorizations, contacts, selectDriverId, students]
  );
  const filteredEntries = useMemo(
    () => filterDriverDirectory(entries, search),
    [entries, search]
  );

  useEffect(() => {
    if (loading) return;
    if (!filteredEntries.length) {
      setSelectedDriverId(null);
      return;
    }
    if (!filteredEntries.some((entry) => Number(entry.driver.id) === Number(selectedDriverId))) {
      setSelectedDriverId(filteredEntries[0].driver.id);
    }
  }, [filteredEntries, loading, selectedDriverId]);

  const selectedEntry = useMemo(
    () => entries.find((entry) => Number(entry.driver.id) === Number(selectedDriverId)) || null,
    [entries, selectedDriverId]
  );

  return (
    <div className="sap-page sad-page">
      {error ? <div className="sa-error sap-inline-error">{error}</div> : null}

      {loading ? (
        <div className="sa-empty">Loading...</div>
      ) : (
        <div className="sap-parents-shell sad-drivers-shell">
          <section className="sap-parents-panel sap-directory-panel">
            <div className="sap-panel-heading">
              <div>
                <h2>Drivers</h2>
                <p>Parent-authorized driver accounts</p>
              </div>
              <span className="sad-directory-count">{filteredEntries.length}/{entries.length}</span>
            </div>

            <div className="sap-directory-list">
              {filteredEntries.length ? filteredEntries.map((entry) => {
                const status = getDriverAuthorizationStatus(entry);
                const details = entry.driver.phone || getUsableEmail(entry.driver) || 'No contact details';
                return (
                  <SchoolDirectoryRow
                    key={entry.driver.id}
                    record={entry.driver}
                    name={formatContactName(entry.driver)}
                    meta={`${status.label} - ${details}`}
                    active={Number(selectedDriverId) === Number(entry.driver.id)}
                    onClick={() => setSelectedDriverId(entry.driver.id)}
                  />
                );
              }) : (
                <div className="sa-empty sap-compact-empty">
                  {entries.length ? 'No drivers match your search.' : 'No authorized driver accounts found for this school.'}
                </div>
              )}
            </div>

            <div className="sap-directory-footer">
              <label className="sap-directory-search">
                <span>⌕</span>
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search driver, parent, student, phone..."
                />
              </label>
            </div>
          </section>

          <section className="sap-parents-panel sap-detail-panel sad-detail-panel">
            <div className="sap-panel-heading">
              <div>
                <h2>Detailed view</h2>
                <p>View and edit an authorized driver account</p>
              </div>
            </div>

            {selectedEntry ? (() => {
              const { driver, parents } = selectedEntry;
              const status = getDriverAuthorizationStatus(selectedEntry);
              const parentById = new Map(parents.map((parent) => [Number(parent.id), parent]));
              const emergencyPhone = /^PARENT_ACCOUNT:/i.test(driver.emergency_backup_phone || '')
                ? null
                : driver.emergency_backup_phone;

              return (
                <div className="sad-driver-detail">
                  <div className="sap-parent-hero">
                    <SchoolAvatar record={driver} enableViewer large />
                    <h3>{formatContactName(driver)}</h3>
                    <span>{driver.phone || 'No phone number'}</span>
                    <span className={getContactEmailDisplay(driver).needsEmail ? 'sap-needs-email' : ''}>
                      {getContactEmailDisplay(driver).label}
                    </span>
                    <span className={`sad-status-badge ${status.tone}`}>{status.label}</span>
                  </div>

                  <div className="sad-actions">
                    <button
                      type="button"
                      className="sa-btn sa-btn-primary"
                      onClick={() => navigate('driver-edit', { driver, schoolId })}
                    >
                      Edit Driver
                    </button>
                    <ResetCodeButton token={token} userType="contact" userId={driver.id} userName={formatContactName(driver)} contact={driver} />
                    <SendOnboardingEmailButton token={token} parentId={driver.id} schoolId={schoolId} hasPassword={driver.has_password} contact={driver} />
                    <SendOnboardingWhatsAppButton token={token} parentId={driver.id} schoolId={schoolId} hasPassword={driver.has_password} />
                  </div>

                  <div className="sad-detail-grid">
                    <div className="sap-section-card">
                      <div className="sap-section-caption">Authorized by ({parents.length})</div>
                      {parents.length ? parents.map((parent) => (
                        <button type="button" className="sad-related-row" key={parent.id} onClick={() => navigate('parents', { schoolId, selectParentId: parent.id })}>
                          <SchoolAvatar record={parent} />
                          <span><strong>{formatContactName(parent)}</strong><small>{parent.phone || getUsableEmail(parent) || 'Parent account'}</small></span>
                          <b>→</b>
                        </button>
                      )) : <div className="sap-section-empty">No authorizing parent is linked.</div>}
                    </div>

                    <div className="sap-section-card">
                      <div className="sap-section-caption">Vehicle information</div>
                      <SchoolInfoRow label="Make / model" value={driver.vehicle_make_model} />
                      <SchoolInfoRow label="Color" value={driver.car_color} />
                      <SchoolInfoRow label="License plate" value={driver.license_plate} />
                      <SchoolInfoRow label="Driver license" value={driver.driver_license_number} />
                    </div>

                    <div className="sap-section-card">
                      <div className="sap-section-caption">Account information</div>
                      <SchoolInfoRow label="Relationship" value={driver.emergency_relationship} />
                      <SchoolInfoRow label="Emergency contact" value={[driver.emergency_f_name, driver.emergency_l_name].filter(Boolean).join(' ')} />
                      <SchoolInfoRow label="Emergency phone" value={emergencyPhone} />
                      <SchoolInfoRow label="Password set" value={driver.has_password ? 'Yes' : 'No'} />
                      <SchoolInfoRow label="Address" value={[driver.street_address, [driver.city, driver.state].filter(Boolean).join(', '), driver.zip_code].filter(Boolean).join(' ')} />
                    </div>
                  </div>

                  <DriverAuthorizedChildren
                    token={token}
                    schoolId={schoolId}
                    driver={driver}
                    rows={buildAuthorizedChildRows(selectedEntry, students)}
                    students={students}
                    parentById={parentById}
                    onChanged={load}
                    onOpenStudent={(studentId) => navigate('students', { schoolId, selectStudentId: studentId })}
                  />
                </div>
              );
            })() : (
              <div className="sa-empty sap-compact-empty">Select a driver to view details.</div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
